/**
 * Поле периода «с — по» с выпадающим двойным календарём.
 *
 * Заменяет пару полей «С» и «По»: первый щелчок ставит начало, второй —
 * окончание, и окно закрывается. Пока окончание не выбрано, промежуток
 * от начала до дня под указателем (или фокусом) закрашивается — видно,
 * что получится. Окончание раньше начала — периоды меняются местами:
 * выбрать «наоборот» нельзя. Наверх уходит только законченный период.
 *
 * Рядом два месяца подряд; на телефоне — один, в нижнем листе.
 * С клавиатуры — как в одиночном календаре (DateInput): стрелки,
 * Home/End, PageUp/PageDown (с Shift — год), Enter или пробел выбирают.
 *
 * С typeable обе даты можно напечатать «ДД.ММ.ГГГГ» прямо в поле —
 * в том числе только одну границу: фильтрам нужен открытый период.
 */

import { Popover as HuiPopover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
} from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";

import { formatDate, todayIso } from "@/shared/lib/date";

import { cn } from "./cn";
import {
  addDays,
  addMonths,
  dayTitle,
  monthName,
  PANEL,
  parseIso,
  toIso,
  useTypedDate,
  WEEKDAYS,
} from "./dateParts";
import { SHEET_PANEL, useSheet } from "./sheet";

/** Границы периода — ISO-даты «ГГГГ-ММ-ДД»; пустая строка — граница не задана. */
export interface DateRangeValue {
  from: string;
  to: string;
}

interface DateRangeInputProps extends DateRangeValue {
  onChange: (value: DateRangeValue) => void;
  /** Раньше этой даты выбрать нельзя. */
  min?: string | undefined;
  /** Позже этой даты выбрать нельзя. */
  max?: string | undefined;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  /** Крестик очистки обеих дат. */
  clearable?: boolean;
  /** Даты можно напечатать «ДД.ММ.ГГГГ», а не только выбрать в календаре. */
  typeable?: boolean;
  placeholder?: string;
  id?: string | undefined;
  "aria-label"?: string | undefined;
  "aria-describedby"?: string | undefined;
  "aria-invalid"?: boolean | undefined;
  className?: string | undefined;
}

/** Подпись периода: «01.09.2026 — 30.09.2026», «с 01.09.2026», «по 30.09.2026». */
function rangeLabel(from: string, to: string): string {
  if (from !== "" && to !== "") return `${formatDate(from)} — ${formatDate(to)}`;
  if (from !== "") return `с ${formatDate(from)}`;
  if (to !== "") return `по ${formatDate(to)}`;
  return "";
}

export function DateRangeInput({
  from,
  to,
  onChange,
  min,
  max,
  disabled = false,
  invalid = false,
  clearable = true,
  typeable = false,
  placeholder = "Выбрать период",
  id,
  "aria-label": ariaLabel,
  "aria-describedby": describedBy,
  "aria-invalid": ariaInvalid,
  className,
}: DateRangeInputProps) {
  const sheet = useSheet();
  const showClear = clearable && (from !== "" || to !== "") && !disabled;
  const typedFrom = useTypedDate(from, (next) => onChange({ from: next, to }), min, max);
  const typedTo = useTypedDate(to, (next) => onChange({ from, to: next }), from === "" ? min : from, max);
  const label = rangeLabel(from, to);

  const clearButton = showClear && (
    <button
      type="button"
      aria-label="Очистить период"
      title="Очистить период"
      onClick={() => onChange({ from: "", to: "" })}
      className={cn(
        "absolute top-1/2 grid size-6 -translate-y-1/2 cursor-pointer place-items-center rounded-sm text-faint",
        typeable ? "right-2" : "right-8",
        "hover:bg-sunken hover:text-fg focus-visible:outline-2 focus-visible:outline-accent",
      )}
    >
      <X className="size-3.5" aria-hidden />
    </button>
  );

  const panel = (
    <PopoverPanel
      {...(sheet
        ? { portal: true, className: cn(PANEL, SHEET_PANEL, "p-3") }
        : { anchor: { to: "bottom start", gap: 6 } as const, className: PANEL })}
    >
      {({ close }) => (
        <RangeCalendar
          from={from}
          to={to}
          min={min}
          max={max}
          months={sheet ? 1 : 2}
          onSelect={(next) => {
            onChange(next);
            close();
          }}
        />
      )}
    </PopoverPanel>
  );

  if (typeable) {
    const input = "h-full min-w-0 flex-1 bg-transparent text-fg tabular-nums outline-none placeholder:text-faint disabled:cursor-not-allowed";
    return (
      <div className={cn("relative", className)}>
        <HuiPopover>
          <div
            className={cn(
              "flex h-9 w-full items-center gap-1 rounded-md border bg-surface pl-1 text-sm transition-colors",
              "focus-within:border-accent",
              disabled && "cursor-not-allowed opacity-60",
              invalid || typedFrom.invalid || typedTo.invalid ? "border-danger" : "border-line",
              showClear ? "pr-9" : "pr-3",
            )}
          >
            <PopoverButton
              disabled={disabled}
              aria-label="Открыть календарь"
              title="Открыть календарь"
              className={cn(
                "grid size-7 shrink-0 cursor-pointer place-items-center rounded-sm text-faint",
                "hover:bg-sunken hover:text-fg focus-visible:outline-2 focus-visible:outline-accent data-open:text-accent",
              )}
            >
              <CalendarDays className="size-4" aria-hidden />
            </PopoverButton>
            <input
              id={id}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="ДД.ММ.ГГГГ"
              maxLength={10}
              disabled={disabled}
              aria-label={ariaLabel === undefined ? "Начало периода" : `${ariaLabel}: начало`}
              aria-describedby={describedBy}
              aria-invalid={ariaInvalid ?? (typedFrom.invalid || undefined)}
              data-value={from}
              value={typedFrom.text}
              onChange={typedFrom.change}
              onBlur={typedFrom.blur}
              className={input}
            />
            <span aria-hidden className="text-faint">
              —
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="ДД.ММ.ГГГГ"
              maxLength={10}
              disabled={disabled}
              aria-label={ariaLabel === undefined ? "Окончание периода" : `${ariaLabel}: окончание`}
              aria-describedby={describedBy}
              aria-invalid={ariaInvalid ?? (typedTo.invalid || undefined)}
              data-value={to}
              value={typedTo.text}
              onChange={typedTo.change}
              onBlur={typedTo.blur}
              className={input}
            />
          </div>
          {clearButton}
          {panel}
        </HuiPopover>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      <HuiPopover>
        <PopoverButton
          id={id}
          disabled={disabled}
          aria-label={ariaLabel === undefined ? undefined : `${ariaLabel}: ${label === "" ? "не выбран" : label}`}
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid}
          data-from={from}
          data-to={to}
          className={cn(
            "flex h-9 w-full cursor-pointer items-center gap-2 rounded-md border bg-surface px-3 text-left text-sm",
            "outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            "focus-visible:border-accent data-open:border-accent",
            invalid ? "border-danger" : "border-line",
            showClear ? "pr-16" : "pr-8",
          )}
        >
          <CalendarDays className="size-4 shrink-0 text-faint" aria-hidden />
          <span className={cn("min-w-0 flex-1 truncate tabular-nums", label === "" ? "text-faint" : "text-fg")}>
            {label === "" ? placeholder : label}
          </span>
        </PopoverButton>
        <ChevronDown
          className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-faint"
          aria-hidden
        />
        {clearButton}
        {panel}
      </HuiPopover>
    </div>
  );
}

/** Первое число месяца даты. */
const monthOf = (iso: string) => `${iso.slice(0, 7)}-01`;

interface RangeCalendarProps extends DateRangeValue {
  min?: string | undefined;
  max?: string | undefined;
  /** Сколько месяцев рядом: два на широком экране, один на телефоне. */
  months: 1 | 2;
  onSelect: (value: DateRangeValue) => void;
}

/** Месяцы подряд с выбором периода в два щелчка. */
export function RangeCalendar({ from, to, min, max, months, onSelect }: RangeCalendarProps) {
  const today = todayIso();
  const outOfRange = (iso: string) => (min !== undefined && iso < min) || (max !== undefined && iso > max);
  // Начальный день: начало периода, его окончание, иначе сегодня — но
  // в пределах min/max: календарь отпусков другого года не должен
  // открываться на сегодняшнем месяце, где выбрать нечего.
  const [focused, setFocused] = useState(() => {
    if (parseIso(from) !== null) return from;
    if (parseIso(to) !== null) return to;
    if (min !== undefined && today < min) return min;
    if (max !== undefined && today > max) return max;
    return today;
  });
  // Левый из видимых месяцев; при выбранном окончании в следующем месяце
  // оно видно сразу справа.
  const [view, setView] = useState(() => monthOf(focused));
  // Начало, выбранное первым щелчком; null — период ещё не начат.
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const moveFocus = useRef(true);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    root.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus();
  }, [focused, view]);

  /** Фокус на день; видимые месяцы сдвигаются, если день за их краем. */
  const focusDay = (iso: string) => {
    moveFocus.current = true;
    setFocused(iso);
    const month = monthOf(iso);
    const last = addMonths(view, months - 1);
    if (month < view) setView(month);
    else if (month > last) setView(addMonths(month, -(months - 1)));
  };

  // Закрашиваемый промежуток: начатый период до дня под указателем
  // или уже выбранный период.
  let low: string | null = null;
  let high: string | null = null;
  if (anchor !== null) {
    const other = hover ?? anchor;
    [low, high] = anchor <= other ? [anchor, other] : [other, anchor];
  } else if (parseIso(from) !== null && parseIso(to) !== null && from <= to) {
    [low, high] = [from, to];
  } else if (parseIso(from) !== null) {
    [low, high] = [from, from];
  } else if (parseIso(to) !== null) {
    [low, high] = [to, to];
  }

  const pick = (iso: string) => {
    if (outOfRange(iso)) return;
    if (anchor === null) {
      setAnchor(iso);
      setHover(iso);
      return;
    }
    setAnchor(null);
    onSelect(anchor <= iso ? { from: anchor, to: iso } : { from: iso, to: anchor });
  };

  const onDayKey = (event: KeyboardEvent<HTMLButtonElement>, iso: string) => {
    const date = parseIso(iso);
    if (date === null) return;
    const weekday = (date.getUTCDay() + 6) % 7;
    const shifts: Record<string, () => string> = {
      ArrowLeft: () => addDays(iso, -1),
      ArrowRight: () => addDays(iso, 1),
      ArrowUp: () => addDays(iso, -7),
      ArrowDown: () => addDays(iso, 7),
      Home: () => addDays(iso, -weekday),
      End: () => addDays(iso, 6 - weekday),
      PageUp: () => addMonths(iso, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(iso, event.shiftKey ? 12 : 1),
    };
    const shift = shifts[event.key];
    if (shift === undefined) return;
    event.preventDefault();
    const next = shift();
    focusDay(next);
    if (anchor !== null) setHover(next);
  };

  // Листание не уводит фокус с кнопки, но день под фокусом остаётся
  // в видимых месяцах: иначе Tab не нашёл бы ни одного дня.
  const flip = (delta: number) => {
    const nextView = addMonths(view, delta);
    setView(nextView);
    const shifted = addMonths(focused, delta);
    setFocused(isVisible(shifted, nextView, months) ? shifted : nextView);
  };

  const navButton =
    "grid h-8 cursor-pointer place-items-center rounded-sm text-muted hover:bg-sunken hover:text-fg " +
    "focus-visible:outline-2 focus-visible:outline-accent";

  return (
    <div ref={root} className="select-none">
      <div className={cn("grid gap-6", months === 2 && "grid-cols-2")}>
        {Array.from({ length: months }, (_, index) => {
          const first = parseIso(addMonths(view, index)) ?? new Date();
          const year = first.getUTCFullYear();
          const month = first.getUTCMonth();
          // Неделя с понедельника: getUTCDay() даёт 0 для воскресенья.
          const offset = (first.getUTCDay() + 6) % 7;
          const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
          const isFirst = index === 0;
          const isLast = index === months - 1;
          return (
            <div key={toIso(first)} className={cn(months === 2 && "w-64")}>
              <div className="mb-2 grid grid-cols-[1.75rem_1.75rem_1fr_1.75rem_1.75rem] items-center gap-px">
                {isFirst ? (
                  <>
                    <button type="button" className={navButton} aria-label="Предыдущий год" onClick={() => flip(-12)}>
                      <ChevronsLeft className="size-4" aria-hidden />
                    </button>
                    <button type="button" className={navButton} aria-label="Предыдущий месяц" onClick={() => flip(-1)}>
                      <ChevronLeft className="size-4" aria-hidden />
                    </button>
                  </>
                ) : (
                  <span className="col-span-2" />
                )}
                <strong className="text-center text-sm font-bold text-fg" aria-live="polite">
                  {monthName(first)}
                </strong>
                {isLast ? (
                  <>
                    <button type="button" className={navButton} aria-label="Следующий месяц" onClick={() => flip(1)}>
                      <ChevronRight className="size-4" aria-hidden />
                    </button>
                    <button type="button" className={navButton} aria-label="Следующий год" onClick={() => flip(12)}>
                      <ChevronsRight className="size-4" aria-hidden />
                    </button>
                  </>
                ) : (
                  <span className="col-span-2" />
                )}
              </div>

              <div className="mb-1 grid grid-cols-7 text-center text-xs text-muted" aria-hidden>
                {WEEKDAYS.map((name) => (
                  <span key={name} className="py-1">
                    {name}
                  </span>
                ))}
              </div>

              {/* Без зазора по горизонтали: заливка периода идёт сплошной полосой. */}
              <div
                role="group"
                aria-label={`Даты месяца: ${monthName(first)}`}
                className="grid grid-cols-7 gap-y-0.5"
                onMouseLeave={() => {
                  if (anchor !== null) setHover(null);
                }}
              >
                {Array.from({ length: offset }, (_, blank) => (
                  <span key={`blank-${String(blank)}`} />
                ))}
                {Array.from({ length: count }, (_, dayIndex) => {
                  const date = new Date(Date.UTC(year, month, dayIndex + 1));
                  const iso = toIso(date);
                  const blocked = outOfRange(iso);
                  const isStart = iso === low;
                  const isEnd = iso === high;
                  const inside = low !== null && high !== null && iso > low && iso < high;
                  return (
                    <button
                      key={iso}
                      type="button"
                      data-date={iso}
                      tabIndex={iso === focused ? 0 : -1}
                      aria-label={dayTitle.format(date)}
                      aria-pressed={isStart || isEnd}
                      // aria-disabled, а не disabled: недоступный день остаётся
                      // в обходе стрелками, иначе фокус на нём терялся бы.
                      aria-disabled={blocked || undefined}
                      {...(iso === today ? { "aria-current": "date" as const } : {})}
                      onClick={() => pick(iso)}
                      onMouseEnter={() => {
                        if (anchor !== null && !blocked) setHover(iso);
                      }}
                      onFocus={() => {
                        if (anchor !== null && !blocked) setHover(iso);
                      }}
                      onKeyDown={(event) => onDayKey(event, iso)}
                      className={cn(
                        "relative min-h-8 cursor-pointer p-0.5 text-sm tabular-nums transition-colors",
                        "focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-accent",
                        isStart || isEnd
                          ? "bg-accent font-bold text-accent-fg"
                          : inside
                            ? "bg-accent-soft text-fg"
                            : "rounded-sm text-fg hover:bg-sunken",
                        isStart && (isEnd ? "rounded-sm" : "rounded-l-sm"),
                        isEnd && !isStart && "rounded-r-sm",
                        iso === today && !isStart && !isEnd && "underline underline-offset-4",
                        blocked && "cursor-not-allowed opacity-40 hover:bg-transparent",
                      )}
                    >
                      {date.getUTCDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-center text-xs text-muted" aria-live="polite">
        {anchor === null ? "Выберите начало периода" : "Выберите окончание периода"}
      </p>
    </div>
  );
}

function isVisible(iso: string, view: string, months: number): boolean {
  const month = monthOf(iso);
  return month >= view && month <= addMonths(view, months - 1);
}
